(() => {
  const config = window.KobuRetailerCartGuardConfig || {};
  const retailerVariantIds = new Set((config.retailerVariantIds || []).map((id) => String(id)));
  const contentCreatorVariantIds = new Set((config.contentCreatorVariantIds || []).map((id) => String(id)));
  const sharedVariantIds = new Set((config.sharedVariantIds || []).map((id) => String(id)));
  const contentCreatorStorageKey = 'kobu-content-creator-variant-ids';

  try {
    const storedVariantIds = JSON.parse(window.localStorage.getItem(contentCreatorStorageKey) || '[]');
    storedVariantIds.forEach((id) => contentCreatorVariantIds.add(String(id)));
  } catch (error) {
    // Local storage can be unavailable in private browsing; configured IDs still work.
  }

  const getCartUrl = () => {
    if (window.routes?.cart_url) return `${window.routes.cart_url}.js`;
    return '/cart.js';
  };

  const getVariantId = (formDataOrVariantId) => {
    if (formDataOrVariantId instanceof FormData) {
      return formDataOrVariantId.get('id');
    }

    if (typeof formDataOrVariantId === 'object' && formDataOrVariantId !== null) {
      return formDataOrVariantId.variantId;
    }

    return formDataOrVariantId;
  };

  const rememberContentCreatorVariant = (variantId) => {
    const normalizedId = String(variantId || '');
    if (!normalizedId) return;

    contentCreatorVariantIds.add(normalizedId);
    try {
      window.localStorage.setItem(
        contentCreatorStorageKey,
        JSON.stringify(Array.from(contentCreatorVariantIds))
      );
    } catch (error) {
      // Local storage can be unavailable in private browsing.
    }
  };

  const isSharedVariant = (variantId) => sharedVariantIds.has(String(variantId || ''));

  const isRetailerVariant = (variantId) =>
    retailerVariantIds.has(String(variantId || '')) && !isSharedVariant(variantId);

  const isContentCreatorVariant = (variantId) =>
    contentCreatorVariantIds.has(String(variantId || '')) && !isSharedVariant(variantId);

  const fetchCart = () =>
    fetch(getCartUrl(), { headers: { Accept: 'application/json' } }).then((response) => response.json());

  const removeItems = (items) => {
    if (!items.length || !window.routes?.cart_update_url) {
      return Promise.resolve();
    }

    const updates = items.reduce((memo, item) => {
      memo[item.key] = 0;
      return memo;
    }, {});

    const requestConfig = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        };

    requestConfig.body = JSON.stringify({ updates });

    return fetch(window.routes.cart_update_url, requestConfig).then((response) => response.json());
  };

  const prepareAdd = (formDataOrVariantId, cartGroup) => {
    const variantId = getVariantId(formDataOrVariantId);
    if (!variantId) return Promise.resolve();

    if (isSharedVariant(variantId)) return Promise.resolve();

    const incomingIsRetailer = isRetailerVariant(variantId);
    const incomingIsContentCreator =
      cartGroup === 'content-creator' || isContentCreatorVariant(variantId);

    if (cartGroup === 'content-creator') {
      rememberContentCreatorVariant(variantId);
    }

    return fetchCart()
      .then((cart) => {
        const itemsToRemove = (cart.items || []).filter((item) => {
          const itemIsRetailer = isRetailerVariant(item.variant_id);
          const itemIsContentCreator = isContentCreatorVariant(item.variant_id);
          const itemIsShared = isSharedVariant(item.variant_id);

          if (incomingIsContentCreator) {
            return !itemIsContentCreator && !itemIsShared;
          }

          if (incomingIsRetailer) {
            return !itemIsRetailer && !itemIsShared;
          }

          return itemIsRetailer || itemIsContentCreator;
        });

        return removeItems(itemsToRemove);
      })
      .catch((error) => {
        console.error('Kobu cart group guard failed', error);
      });
  };

  const bindNativeCartForms = () => {
    document.querySelectorAll('form[data-kobu-cart-group-form]').forEach((form) => {
      if (form.dataset.kobuCartGroupBound === 'true') return;
      form.dataset.kobuCartGroupBound = 'true';

      form.addEventListener('submit', async (event) => {
        if (form.dataset.kobuCartGroupSubmitting === 'true') return;

        const variantId = form.querySelector('[name="id"]')?.value;
        if (!variantId) return;

        event.preventDefault();
        form.dataset.kobuCartGroupSubmitting = 'true';

        try {
          await prepareAdd(variantId);
        } finally {
          HTMLFormElement.prototype.submit.call(form);
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindNativeCartForms, { once: true });
  } else {
    bindNativeCartForms();
  }

  window.KobuCartGroupGuard = {
    prepareAdd,
    isRetailerVariant,
    isContentCreatorVariant,
    isSharedVariant,
  };
})();
